// src/components/ImageGalleryCard.tsx
'use client';

import React from 'react';
import Image from 'next/image';
import PixelatedImage from '@/components/media/PixelatedImage';
import Link from 'next/link';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toAbsoluteMediaUrl, maybeCacheBust } from '@/lib/media';
import { toCdn, feedThumb } from '@/lib/imageUrls';
import { scrubContactInfo } from '@/lib/scrubContact';
import { Lock } from 'lucide-react';
import AudienceBadge from '@/components/content/AudienceBadge';
import { isPrivateAudience } from '@/lib/audience';
// NO CLIENT-SIDE GATING — the API returns correct image URLs per viewer.
// See: docs/CONTENT-GATING-SPEC.md.
// The audience pill below is presentation only: a row only reaches this card
// because the API already decided the viewer is inside its audience.

export type GalleryImage =
  | { imageFeedId?: number | string; location?: string; approval?: string | null }
  | { [k: string]: any }
  | string;

export interface ImageGalleryCardProps {
  galleryId: number;
  name?: string;
  description?: string;
  images?: GalleryImage[];
  updatedAt?: string | Date | null;
  /** When true, blur entire gallery for guests regardless of per-image flags */
  unsafe?: boolean;
  /** When true, gallery has images held pending ID verification */
  pendingVerification?: boolean;
  /** Number of comments on this gallery; rendered next to the updated-at line when defined */
  commentCount?: number;
  /** 'Public' | 'Friends' | 'Watchers'. Friends/Watchers show a small pill; Public (or absent) shows nothing. */
  audience?: string;
}


export default function ImageGalleryCard({
  galleryId,
  name,
  description,
  images,
  updatedAt,
  unsafe = false,
  pendingVerification = false,
  commentCount,
  audience,
}: ImageGalleryCardProps) {
  const title = String(name ?? '');
  const desc = String(description ?? '');
  const shortTitle = title.slice(0, 80);

  const imgArray = Array.isArray(images) ? images : [];
  // Tiny transparent placeholder for blur while Next optimizes image
  const BLUR = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==' as const;
  const isChatMediaPath = (s?: string | null): boolean => {
    if (!s) return false;
    try {
      const raw = String(s);
      const p = /^https?:\/\//i.test(raw) ? new URL(raw).pathname : raw;
      const lower = p.toLowerCase();
      return (
        lower.includes('/chat/') ||
        lower.includes('/chat-') ||
        lower.includes('chat-library') ||
        lower.includes('chatlibrary') ||
        lower.includes('/library/chat') ||
        /(^|\/)chat(images|_images)?(\/|$)/.test(lower)
      );
    } catch { return false; }
  };
  // Build first 3 candidate image keys + explicit flags (do not hide; guests will see blurred)
  const pairs: { raw: string; explicit: boolean; pending: boolean; pending2257: boolean }[] = [];
  for (const img of imgArray) {
    try {
      let explicit = false;
      let pending = false;
      let pending2257 = false;
      let chosen = '';
      if (typeof img === 'string') {
        chosen = String(img);
      } else {
        const anyImg: any = img || {};
        explicit = Boolean(anyImg.isUnsafeContent ?? anyImg.IsUnsafeContent ?? anyImg.unsafe ?? anyImg.Unsafe ?? false);
        pending2257 = anyImg.approval === 'pending_2257';
        pending = anyImg.approval === 'pending_verification' || pending2257;
        const candidates = [
          anyImg.signedThumbnailUrl, anyImg.SignedThumbnailUrl,
          anyImg.thumbnailUrl, anyImg.ThumbnailUrl,
          anyImg.thumbUrl, anyImg.ThumbUrl,
          anyImg.thumbnailKey, anyImg.thumbKey,
          anyImg.thumbnailLocation, anyImg.ThumbnailLocation,
          anyImg.thumbLocation, anyImg.ThumbLocation,
          anyImg.thumbnailPath, anyImg.thumbPath,
          anyImg.location, anyImg.Location,
          anyImg.key, anyImg.Key,
          anyImg.path, anyImg.Path,
        ];
        chosen = String(candidates.find((v) => typeof v === 'string' && v.trim().length > 0) || '');
        if (!chosen) continue;
      }
      const isAbs = /^https?:\/\//i.test(chosen);
      let key = chosen;
      if (!isAbs) {
        const cleaned = chosen.replace(/^\/+/, '');
        key = /^(image-feed|thumbnails|video-thumbs|thumbs)\//i.test(cleaned) ? cleaned : feedThumb(cleaned);
      }
      if (!key || isChatMediaPath(String(key))) continue;
      pairs.push({ raw: key, explicit, pending, pending2257 });
      if (pairs.length >= 3) break;
    } catch { /* skip */ }
  }

  const raws = pairs.map(p => p.raw);
  const expFlags = pairs.map(p => p.explicit);
  const pendingFlags = pairs.map(p => p.pending);

  const dateText = (() => {
    try {
      if (!updatedAt) return '';
      const d = new Date(updatedAt as any);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch { return ''; }
  })();

  const mapPreferred = (r: string, idx: number) => {
    // API handles blur server-side — just use URLs as-is
    const s = String(r || '');
    // If absolute and points to an image-feed path, normalize to feed CDN
    try {
      const u = new URL(s);
      // Get expected CDN hostname from environment
      const expectedCdnHost = (process.env.NEXT_PUBLIC_CDN_HOSTNAME || 'd14nsfeg9ivjwi.cloudfront.net').toLowerCase();
      // If URL already points to the correct CDN, return as-is (don't rewrite)
      if (u.hostname.toLowerCase() === expectedCdnHost) {
        return s;
      }
      const path = u.pathname.replace(/^\/+/, '');
      if (/^(image-feed|thumbnails|video-thumbs|thumbs)\//i.test(path)) {
        // Rewrite to configured feed CDN directly (matches front page)
        return toCdn(path, 'feed');
      }
      // Non image-feed absolute URL: use as-is
      return s;
    } catch {}
    // Not absolute: prefer resolver when it looks like an image-feed key
    const cleaned = s.replace(/^\/+/, '');
    if (/^(image-feed|thumbnails|video-thumbs|thumbs)\//i.test(cleaned)) {
      return toCdn(cleaned, 'feed');
    }
    // Default: derive a feed thumb and map to CDN
    const fallbackThumb = feedThumb(cleaned);
    return /^https?:\/\//i.test(fallbackThumb) ? fallbackThumb : toCdn(fallbackThumb.replace(/^\/+/, ''), 'feed');
  };
  const primary = raws.map((r, idx) => mapPreferred(r, idx));
  const [srcs, setSrcs] = React.useState<string[]>(primary);
  // Guest-gated gallery: the server sent _blurred variants. Label it like the
  // talent/video cards so the mosaic blur reads as a membership perk, not a
  // broken image (re-score 07-11). Presentation only; gating stays server-side.
  const isBlurred = srcs.some((s) => /_blurred/i.test(String(s || '')));
  const [attempted, setAttempted] = React.useState<number[]>(() => raws.map(() => 0));
  // no action bar on cards

  React.useEffect(() => {
    const updated = raws.map((r, idx) => mapPreferred(r, idx));
    setSrcs(updated);
    setAttempted(raws.map(() => 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [galleryId, name, description, JSON.stringify(raws)]);

  // Removed proactive resolver swap: keep initial absolute/CDN URLs
  // and only switch to the resolver on actual load error via onImgError.

  const onImgError = React.useCallback((idx: number) => {
    const att = attempted[idx] || 0;

    if (att >= 3) return; // give up after a few fallbacks
    const raw = String(raws[idx] || '');
    if (!raw) return;
    (async () => {
      try {
        let cleaned = raw.replace(/^\/+/, '');
        try {
          const u = new URL(raw);
          cleaned = u.pathname.replace(/^\/+/, '');
        } catch {}
        let alt = '';
        if (att === 0) {
          // First fallback: normalize to feed CDN
          alt = toCdn(cleaned, 'feed');
        } else if (att === 1) {
          // Second fallback: use resolver (lets API choose CDN)
          alt = `/media/resolve?k=${encodeURIComponent(cleaned)}&area=feed`;
        } else {
          // Third fallback: strip _thumb to try original on feed CDN
          const orig = cleaned.replace(/_thumb(\.[a-z0-9]+)$/i, '$1');
          alt = toCdn(orig, 'feed') || toAbsoluteMediaUrl('/' + orig);
        }
        setSrcs((prev) => {
          const next = [...prev];
          next[idx] = alt;
          return next;
        });
      } finally {
        setAttempted((a) => {
          const next = [...a];
          next[idx] = (a[idx] || 0) + 1;
          return next;
        });
      }
    })();
  }, [attempted, raws]);

  // If no valid images, render placeholders instead of skipping

  return (
    <Link href={`/gallery/${galleryId}`} className="block">
      <Card className="py-2 w-full flex flex-col gap-1 rounded-2xl border border-zinc-200/60 dark:border-zinc-800 shadow-sm hover:shadow-md transition-shadow duration-200">
        <CardHeader className="px-4 pt-2">
          <div className="min-h-[56px] flex flex-col justify-between">
            <CardTitle className="font-serif text-lg font-semibold tracking-tight leading-tight line-clamp-2">{shortTitle ? scrubContactInfo(shortTitle) : '\u00A0'}</CardTitle>
            <CardDescription className="mt-1 line-clamp-2">
              {desc ? scrubContactInfo(desc) : '\u00A0'}
            </CardDescription>
          </div>
        </CardHeader>

        <div className="relative grid grid-cols-3 gap-1 px-4 pb-4 rounded-[inherit] overflow-hidden h-[192px] items-stretch">
          {srcs.length > 0 ? (
            srcs.map((src, idx) => {
              const isPending = pendingFlags[idx] || false;
              return (
              <div key={idx} className="relative h-full w-full rounded-lg overflow-hidden">
                <PixelatedImage
                  src={src || '/img/galaxy.svg'}
                  alt={`${name || 'Gallery'} image ${idx + 1}`}
                  placeholderSrc={BLUR}
                  onError={() => onImgError(idx)}
                />
                {isPending && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <span className={`text-[9px] font-medium text-white px-1.5 py-0.5 rounded ${pairs[idx]?.pending2257 ? 'bg-red-600' : 'bg-amber-600'}`}>
                      {pairs[idx]?.pending2257 ? 'Pending 2257 Compliance' : 'Pending Verification'}
                    </span>
                  </div>
                )}
              </div>
            )})
          ) : (
            // Skeleton placeholders when no images yet
            Array.from({ length: 3 }).map((_, idx) => (
              <div key={`sk-${idx}`} className="relative h-full w-full rounded-lg overflow-hidden">
                <div className="skeleton absolute inset-0" />
              </div>
            ))
          )}
          {isPrivateAudience(audience) && (
            // Top-left so it never collides with the Lock pill at top-right.
            <AudienceBadge
              audience={audience}
              className="absolute top-2 left-4 z-10 border-0 bg-black/55 text-white backdrop-blur-sm text-[11px]"
            />
          )}
          {isBlurred && (
            <span className="absolute top-2 right-4 z-10 inline-flex items-center gap-1 rounded-full bg-black/55 backdrop-blur-sm px-2 py-0.5 text-[11px] font-medium text-white">
              <Lock className="h-3 w-3" aria-hidden="true" />
              <span>Join free to view</span>
            </span>
          )}
          {pendingVerification && (
            <div className="absolute bottom-1 right-1 z-10">
              <span className={`text-[9px] font-medium text-white px-1.5 py-0.5 rounded ${pairs.some(p => p.pending2257) ? 'bg-red-600/90' : 'bg-amber-600/90'}`}>
                {pairs.some(p => p.pending2257) ? 'Pending 2257 Compliance' : 'Pending Verification'}
              </span>
            </div>
          )}
        </div>
        <div className="px-4 pb-3 text-xs text-muted-foreground flex items-center gap-3">
          {dateText && (<span>Updated {dateText}</span>)}
          {typeof commentCount === 'number' && (
            <span className="inline-flex items-center gap-1" aria-label={`${commentCount} comments`}>
              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              <span>{commentCount}</span>
            </span>
          )}
        </div>
      </Card>
    </Link>
  );
}
